/*
 * POC: Klaviyo Back-in-Stock replacement for Biscuit Bundles submit button.
 *
 * Hooks into the BiscuitsBundleForm:ready event (same pattern as
 * biscuit-bundler.js) so the full BB step UI stays live. When a product
 * carries the `notify-when-back-in-stock` tag, the native theme submit
 * button is re-purposed: text/behavior change, click opens an inline
 * email-capture form, submission POSTs to Klaviyo's client-side
 * back-in-stock-subscriptions endpoint.
 *
 * Data is read from <script id="klaviyo-bis-data"> (injected by the
 * klaviyo-back-in-stock.liquid snippet).
 */
document.addEventListener('BiscuitsBundleForm:ready', (e) => {
  const dataEl = document.getElementById('klaviyo-bis-data');
  if (!dataEl) return;

  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.warn('[klaviyo-bis] bad config', err);
    return;
  }

  if (!config.enabled || !config.companyId || !config.variantId) return;

  const bundleForm = e.target;
  const submitBtn = bundleForm.submitButtons && bundleForm.submitButtons[0];
  if (!submitBtn) return;

  // Biscuit Bundles prepends a ".product-form__submit-price" span and keeps
  // references to the original text/price spans for later updates. Wiping
  // the button's innerHTML orphans those references so BB's subsequent
  // price writes land on detached nodes and do not appear. Loading spinner
  // is also dropped — the button no longer submits the cart form.
  submitBtn.innerHTML =
    '<span class="product-form__submit-text">Notify Me</span>';

  submitBtn.type = 'button';

  // BB re-disables the submit button after our hook runs (e.g. when the
  // underlying bundle is sold out, BB sets `disabled` and adds the
  // `disabled` / `biscuits--disabled` classes). Keep the button enabled
  // by undoing those mutations whenever they reappear.
  const keepEnabled = () => {
    if (submitBtn.disabled) submitBtn.disabled = false;
    if (submitBtn.hasAttribute('disabled')) submitBtn.removeAttribute('disabled');
    if (submitBtn.classList.contains('disabled')) submitBtn.classList.remove('disabled');
    if (submitBtn.classList.contains('biscuits--disabled')) submitBtn.classList.remove('biscuits--disabled');
  };
  keepEnabled();
  new MutationObserver(keepEnabled).observe(submitBtn, {
    attributes: true,
    attributeFilter: ['disabled', 'class'],
  });

  // Render form markup directly under the CTA so it appears between the
  // submit button and SHIP TO MULTIPLE ADDRESSES, not below the entire
  // BB step UI.
  const formHost = document.createElement('div');
  formHost.className = 'klaviyo-bis-host';
  formHost.hidden = true;
  formHost.innerHTML = `
    <form class="klaviyo-bis-form" novalidate>
      <label for="klaviyo-bis-email" class="klaviyo-bis-label">
        Enter your email and we'll let you know when this is back:
      </label>
      <div class="klaviyo-bis-row">
        <input
          type="email"
          id="klaviyo-bis-email"
          name="email"
          required
          autocomplete="email"
          placeholder="you@example.com"
          class="klaviyo-bis-input"
        >
        <button type="submit" class="klaviyo-bis-submit button button--primary">
          Notify Me
        </button>
      </div>
      <p class="klaviyo-bis-disclaimer">
        By requesting a restock alert, you agree to join our newsletter.
      </p>
      <p class="klaviyo-bis-message" role="status" aria-live="polite"></p>
    </form>
  `;

  submitBtn.insertAdjacentElement('afterend', formHost);

  // Intercept click on the (repurposed) submit button: don't let it submit
  // the Shopify cart form — toggle the notify form instead.
  submitBtn.addEventListener(
    'click',
    (ev) => {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      formHost.hidden = !formHost.hidden;
      submitBtn.classList.toggle('klaviyo-bis-active', !formHost.hidden);
      if (!formHost.hidden) {
        formHost.querySelector('.klaviyo-bis-input').focus();
      }
    },
    { capture: true }
  );

  const form = formHost.querySelector('.klaviyo-bis-form');
  const msgEl = formHost.querySelector('.klaviyo-bis-message');
  const submitInput = formHost.querySelector('.klaviyo-bis-submit');

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const email = formHost.querySelector('.klaviyo-bis-input').value.trim();
    if (!email) return;

    submitInput.disabled = true;
    msgEl.textContent = 'Submitting…';
    msgEl.style.color = '';

    try {
      await subscribe({
        email,
        variantId: config.variantId,
        companyId: config.companyId,
      });
      msgEl.textContent = "You're on the list. We'll email you when it's back.";
      msgEl.style.color = '#0a7a2f';
      form.reset();
    } catch (err) {
      console.error('[klaviyo-bis] subscribe failed', err);
      msgEl.textContent = 'Something went wrong. Please try again.';
      msgEl.style.color = '#dd1d1d';
    } finally {
      submitInput.disabled = false;
    }
  });

  async function subscribe({ email, variantId, companyId }) {
    // Klaviyo expects a catalog-variant id shaped like:
    // $shopify:::$default:::<variantId>
    const klaviyoVariantId = `$shopify:::$default:::${variantId}`;
    const res = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(
        companyId
      )}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: klaviyoVariantId },
              },
            },
          },
        }),
      }
    );

    if (!res.ok && res.status !== 202) {
      const body = await res.text();
      throw new Error(`Klaviyo ${res.status}: ${body}`);
    }
  }
});
